import { useState, useRef } from "react";

/**
 * useFormHybrid - A flexible hook that manages both stateful and ref-based form data.
 *
 * @param {Object} config
 * @param {Object} config.initialState - reactive state values (affect UI)
 * @param {Object} config.initialRefs - non-reactive values (do not affect UI)
 * @param {Function} config.onSubmit - async function to call with combined data
 *
 * @returns {{
 *   state: Object,
 *   setState: Function,
 *   refs: Object,
 *   handleChange: Function,
 *   handleSubmit: Function,
 *   isSubmitting: boolean
 * }}
 */
export function useFormHybrid({
  initialState = {},
  initialRefs = {},
  onSubmit,
}) {
  const [state, setState] = useState(initialState);
  const refs = useRef({ ...initialRefs });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handles updates for stateful fields
  const handleChange = (e) => {
    const { name, value, type, checked, dataset } = e.target;
    const isRef = dataset.ref === "true";

    if (isRef) {
      refs.current[name] = type === "checkbox" ? checked : value;
    } else {
      setState((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Combines and submits data to the provided API
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const payload = { ...state, ...refs.current };

    try {
      setIsSubmitting(true);
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { state, setState, refs, handleChange, handleSubmit, isSubmitting };
}
